import React from 'react';
import MarcEtapa from './MarcEtapa';
import { useTranslation } from 'react-i18next';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  JUDICIS, MENES, candidats, netejaJudicis,
  casella, perCaselles, acordats, avisos, contraQue,
} from '../../canvisSSM';

// L'ETAPA 6: ELS CANVIS.
//
// Les regles són a `src/canvisSSM.js`, amb proves pròpies (`npm run prova:canvis`).
//
// **Els dos eixos són independents, i aquesta és tota la lliçó.** Un canvi pot ser bona idea i no
// passar mai; un altre pot ser fàcil i no servir de res. A l'etapa 7 només hi van els que
// compleixen les dues coses.
//
// I la casella que ensenya més **no és la dels dos sís**: és **desitjable però no factible**.
// Allà no hi ha cap fracàs, hi ha una troballa —què hauria de canviar abans—, i per això aquella
// és l'única que demana el «què caldria».

const CASELLES = ['endavant', 'bloquejat', 'inutil', 'fora', 'pendent'];

const COLORS = {
  endavant: 'border-ok-400 bg-ok-50',
  bloquejat: 'border-warn-400 bg-warn-50',
  inutil: 'border-gray-300 bg-gray-50',
  fora: 'border-gray-200 bg-white',
  pendent: 'border-gray-200 bg-white',
};

const ChangesStage = ({ data, onUpdate }) => {
  const { t } = useTranslation();
  const canvis = data.changes || {};
  const llista = candidats(data);
  const grups = perCaselles(llista);
  const ctx = contraQue(data);
  const problemes = avisos(data);

  const jutja = (id, camp, valor) => onUpdate({ changes: { ...canvis,
    judgements: { ...(canvis.judgements || {}),
      [id]: { ...(canvis.judgements || {})[id], [camp]: valor } } } });

  const afegeix = () => onUpdate({ changes: { ...canvis,
    candidates: [...(canvis.candidates || []), { id: `cv_${Date.now()}`, text: '' }] } });

  const posaText = (id, text) => onUpdate({ changes: { ...canvis,
    candidates: (canvis.candidates || []).map((c) => (c.id === id ? { ...c, text } : c)) } });

  const treu = (id) => onUpdate({ changes: { ...canvis,
    candidates: (canvis.candidates || []).filter((c) => c.id !== id) } });

  const Tria = ({ c, camp }) => (
    <div className="flex gap-1">
      {JUDICIS.map((v) => (
        <button key={v} type="button"
          onClick={() => jutja(c.id, camp, c[camp] === v ? '' : v)}
          className={`px-2 py-1 rounded-md text-[11px] font-medium border transition-colors ${
            c[camp] === v ? 'bg-primary-700 text-white border-primary-700'
                          : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
          {t(`ssm.cvJ${v}`)}
        </button>
      ))}
    </div>
  );

  return (
    <MarcEtapa className="p-6">
      <div className="max-w-6xl mx-auto space-y-5">
        <div className="bg-white border border-gray-200 rounded-md p-4">
          <p className="text-sm text-gray-700">{t('ssm.cvQue')}</p>
        </div>

        {/* EL CONTEXT CONTRA EL QUAL ES JUTJA LA FACTIBILITAT.
            Ve de les Anàlisis 2 i 3, i és el motiu pel qual aquelles es van fer abans d'aquesta
            etapa: sense elles, «culturalment factible» és una casella on escriure el que un
            s'imagini. */}
        {(ctx.papers.length > 0 || ctx.poder.length > 0 || ctx.amos.length > 0) && (
          <div className="bg-white border border-gray-200 rounded-md p-4">
            <h3 className="text-sm font-semibold text-gray-900">{t('ssm.cvContra')}</h3>
            <p className="text-xs text-gray-500 mt-0.5 mb-3">{t('ssm.cvContraWhy')}</p>
            <div className="grid md:grid-cols-3 gap-4 text-xs">
              {[[t('ssm.an2'), ctx.papers.map((p) => p.nom)],
                [t('ssm.an3'), ctx.poder.map((p) => p.moneda || p.com)],
                [t('ssm.an1_issueOwners'), ctx.amos]].map(([titol, valors]) => (
                <div key={titol}>
                  <span className="block font-medium text-gray-700 mb-1">{titol}</span>
                  {valors.length ? (
                    <ul className="space-y-0.5 text-gray-600">
                      {valors.map((v, i) => <li key={i}>· {v}</li>)}
                    </ul>
                  ) : <span className="text-gray-400">—</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {problemes.length > 0 && (
          <ul className="space-y-1.5">
            {problemes.map((p, i) => (
              <li key={i} className="flex items-center gap-3 px-4 py-2.5 rounded-md bg-white
                                     border border-warn-400 text-sm text-gray-700">
                <span>{t(p.clau, { n: p.n })}</span>
                {p.clau === 'ssm.cvOrfes' && (
                  <button type="button"
                    onClick={() => onUpdate({ changes: { ...canvis, judgements: netejaJudicis(data) } })}
                    className="ml-auto text-xs font-medium text-primary-700 hover:underline">
                    {t('ssm.cpNetejaOrfes')}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {llista.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-md p-6">
            <p className="text-gray-700">{t('ssm.cvCapCandidat')}</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-md overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left">
                  <th className="px-3 py-2 font-semibold text-gray-700">{t('ssm.cvCanvi')}</th>
                  <th className="px-3 py-2 font-semibold text-gray-700 w-44">{t('ssm.cvDesitjable')}</th>
                  <th className="px-3 py-2 font-semibold text-gray-700 w-44">{t('ssm.cvFactible')}</th>
                  <th className="px-3 py-2 font-semibold text-gray-700 w-40">{t('ssm.cvMena')}</th>
                  <th className="px-3 py-2 w-8" />
                </tr>
              </thead>
              <tbody>
                {llista.map((c) => (
                  <tr key={c.id} className="border-b border-gray-100 align-top">
                    <td className="px-3 py-2">
                      {c.delModel ? (
                        <>
                          <span className="text-gray-900">{c.text}</span>
                          <span className="block text-xs text-gray-500 mt-0.5">{t(c.motiu)}</span>
                        </>
                      ) : (
                        <input value={c.text || ''} onChange={(e) => posaText(c.id, e.target.value)}
                          placeholder={t('ssm.cvCanviPh')}
                          className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm
                                     focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                      )}
                      {/* Només la casella «desitjable i no factible» demana el «què caldria»:
                          és on hi ha la troballa. */}
                      {casella(c) === 'bloquejat' && (
                        <textarea rows={2} value={c.why || ''}
                          onChange={(e) => jutja(c.id, 'why', e.target.value)}
                          placeholder={t('ssm.cvQueCaldriaPh')}
                          className="mt-2 w-full px-2 py-1 border border-warn-400 rounded-md text-sm
                                     focus:ring-2 focus:ring-primary-500" />
                      )}
                    </td>
                    <td className="px-3 py-2"><Tria c={c} camp="desirable" /></td>
                    <td className="px-3 py-2"><Tria c={c} camp="feasible" /></td>
                    <td className="px-3 py-2">
                      <select value={c.mena || ''} onChange={(e) => jutja(c.id, 'mena', e.target.value)}
                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm
                                   focus:ring-2 focus:ring-primary-500">
                        <option value="">{t('ssm.cvMenaTria')}</option>
                        {MENES.map((m) => <option key={m} value={m}>{t(`ssm.cvMena_${m}`)}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      {!c.delModel && (
                        <button type="button" onClick={() => treu(c.id)}
                          className="p-1 text-gray-400 hover:text-gray-600" aria-label={t('common.remove')}>
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <button type="button" onClick={afegeix}
          className="flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline">
          <PlusIcon className="h-4 w-4" />{t('ssm.cvAfegir')}
        </button>

        {/* LES QUATRE CASELLES. És on es veu que els dos eixos són independents, i que
            «desitjable però no factible» no és cap fracàs. */}
        {llista.some((c) => casella(c) !== 'pendent') && (
          <div className="grid md:grid-cols-2 gap-3">
            {CASELLES.filter((k) => k !== 'pendent' && grups[k].length > 0).map((k) => (
              <div key={k} className={`rounded-md border p-4 ${COLORS[k]}`}>
                <h4 className="text-sm font-semibold text-gray-900">{t(`ssm.cvQ_${k}`)}</h4>
                <p className="text-xs text-gray-600 mt-0.5 mb-2">{t(`ssm.cvQ_${k}d`)}</p>
                <ul className="space-y-1 text-sm text-gray-800">
                  {grups[k].map((c) => (
                    <li key={c.id}>· {c.text || t('ssm.untitled')}
                      {c.mena && <span className="text-xs text-gray-500"> ({t(`ssm.cvMena_${c.mena}`)})</span>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {acordats(llista).length > 0 && (
          <p className="text-sm text-gray-700 bg-ok-50 border border-ok-400 rounded-md px-4 py-2.5">
            {t('ssm.cvCapALaSeteN', { n: acordats(llista).length })}
          </p>
        )}
      </div>
    </MarcEtapa>
  );
};

export default ChangesStage;
